import {
  Archive,
  CalendarClock,
  CheckCircle2,
  CirclePlus,
  FileX,
  MessageSquare,
  Paperclip,
  PlayCircle,
  RefreshCw,
  UserCog,
  UserMinus,
  UserPlus,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { TarefaLogEvento } from "@workflow/types";

interface TarefaLogEventoConfig {
  label: string;
  icon: LucideIcon;
  /** Um tom só, reusado no círculo do ícone e no texto do rótulo — nunca duas paletas pro mesmo evento. */
  tone: string;
}

/**
 * Vocabulário visual do histórico de tarefa: rótulo + ícone + cor por evento.
 * Onde o evento já tem status equivalente (`INICIADA`→`EM_EXECUCAO`, `CONCLUIDA`→`CONCLUIDO`...),
 * reusa o mesmo tom de `TAREFA_STATUS_COLOR_MAP` em vez de inventar uma segunda paleta pro mesmo
 * significado — ver docs do diagnóstico de histórico.
 */
export const TAREFA_LOG_EVENTO_CONFIG: Record<TarefaLogEvento, TarefaLogEventoConfig> = {
  [TarefaLogEvento.CRIADA]: {
    label: "Criada",
    icon: CirclePlus,
    tone: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
  [TarefaLogEvento.INICIADA]: {
    label: "Em execução",
    icon: PlayCircle,
    tone: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
  },
  [TarefaLogEvento.CONCLUIDA]: {
    label: "Concluída",
    icon: CheckCircle2,
    tone: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300",
  },
  [TarefaLogEvento.CANCELADA]: {
    label: "Cancelada",
    icon: XCircle,
    tone: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
  },
  [TarefaLogEvento.ARQUIVADA]: {
    label: "Arquivada",
    icon: Archive,
    tone: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  },
  [TarefaLogEvento.REABERTA]: {
    label: "Reaberta",
    icon: RefreshCw,
    tone: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300",
  },
  [TarefaLogEvento.REATRIBUIDA]: {
    label: "Reatribuída",
    icon: UserCog,
    tone: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
  },
  [TarefaLogEvento.PRAZO_ALTERADO]: {
    label: "Prazo alterado",
    icon: CalendarClock,
    tone: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300",
  },
  [TarefaLogEvento.ANEXO_ADICIONADO]: {
    label: "Anexo adicionado",
    icon: Paperclip,
    tone: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
  [TarefaLogEvento.ANEXO_REMOVIDO]: {
    label: "Anexo removido",
    icon: FileX,
    tone: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
  [TarefaLogEvento.OBSERVADOR_ADICIONADO]: {
    label: "Observador adicionado",
    icon: UserPlus,
    tone: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
  },
  [TarefaLogEvento.OBSERVADOR_REMOVIDO]: {
    label: "Observador removido",
    icon: UserMinus,
    tone: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
  },
  [TarefaLogEvento.MENSAGEM_ADICIONADA]: {
    label: "Comentário",
    icon: MessageSquare,
    tone: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
};
